import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Input from '@mui/material/Input';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import {
  useChecklessSettings,
  useIsTipEditablePostClose
} from 'reservations/account/providers/ConfigProvider';
import { BaseDrawer } from 'reservations/base/components/BaseDrawer';
import { useModal } from 'reservations/base/providers/ModalProvider';
import { getDefaultTipValue } from 'reservations/checklessPayments/helpers';
import { useEnrollChecklessMutation } from 'reservations/checklessPayments/queries/useEnrollChecklessMutation';

export const SelectGratuityModal = () => {
  const {
    close,
    modalContext: { handleSuccess }
  } = useModal();
  const {
    control,
    handleSubmit: handleSubmitForm,
    setValue,
    watch
  } = useFormContext();
  const isTipEditablePostClose = useIsTipEditablePostClose();
  const { tip_amounts: tipAmounts, default_tip: defaultTip } =
    useChecklessSettings();
  const tipPercentage = watch('tipPercentage');
  const { isCustom, tipValue } = getDefaultTipValue(
    tipPercentage ?? defaultTip,
    tipAmounts
  );
  const [showCustom, setShowCustom] = useState<boolean>(isCustom);

  //TODO: Add handle save failed logic
  const mutation = useEnrollChecklessMutation(handleSuccess ?? close, () => {});
  const handleSubmit = async () =>
    handleSubmitForm((data: Record<string, any>) => mutation.mutate(data))();

  const defaultTipRadios = tipAmounts
    .sort((a, b) => Number(a) - Number(b))
    .map(value => (
      <FormControlLabel
        key={value}
        value={value}
        control={<Radio />}
        label={`${value}%`}
        onChange={() => setShowCustom(false)}
      />
    ));

  const isValidNumericInput = (testValue: any) => !isNaN(Number(testValue));

  const disableButton =
    tipPercentage === '' ||
    tipPercentage === 'custom' ||
    !isValidNumericInput(tipPercentage) ||
    tipPercentage < 0 ||
    tipPercentage > 100;

  return (
    <BaseDrawer
      header="Select your gratuity"
      primaryButton={{ disabled: disableButton, onClick: handleSubmit }}
      subheader={`Choose a gratuity amount to apply to your check — no need to calculate it at the table. Your gratuity selection only affects your portion of the bill and can be changed ${isTipEditablePostClose ? 'up to an hour after your visit.' : 'anytime before the meal ends.'}`}
      requireAuth
    >
      <Box>
        <FormControl>
          <Controller
            name="tipPercentage"
            control={control}
            render={({ field: { onChange, name } }) => (
              <RadioGroup
                defaultValue={isCustom ? 'custom' : tipValue}
                aria-labelledby="tip-percentage-select-label"
                name={name}
                onChange={e => onChange(Number(e.target.value))}
              >
                {defaultTipRadios}
                <FormControlLabel
                  value="custom"
                  control={<Radio onChange={() => setShowCustom(true)} />}
                  label="Custom"
                />
              </RadioGroup>
            )}
          />
          {showCustom && (
            <Box maxWidth="100px" marginTop="0.5rem">
              <Input
                autoFocus
                data-testid="tip-custom-input"
                type="text"
                inputMode="numeric"
                defaultValue={
                  isCustom && isValidNumericInput(tipPercentage)
                    ? tipPercentage
                    : ''
                }
                placeholder="0"
                sx={theme => ({
                  color: theme.typography.body1.color,
                  '&::before,': {
                    borderBottom: `1px solid ${theme.typography.body1.color}`
                  },
                  '&:hover:not(.Mui-disabled, .Mui-error):before': {
                    borderBottom: `2px solid ${theme.typography.body1.color}`
                  }
                })}
                onChange={e => {
                  if (isNaN(Number(e.target.value))) {
                    e.target.value = isValidNumericInput(tipPercentage)
                      ? tipPercentage
                      : '';
                  } else if (e.target.value.trim() == '') {
                    setValue('tipPercentage', '');
                    e.target.value = '';
                  } else {
                    setValue('tipPercentage', Number(e.target.value));
                  }
                }}
                endAdornment={<Typography>%</Typography>}
              />
            </Box>
          )}
        </FormControl>
      </Box>
    </BaseDrawer>
  );
};
